import { db } from "./DBHelper";
import ItemService from "services/ItemService";
import { STORAGE_KEYS, COLLECTIONS, PRODUCT_TYPE, EVENTS } from "../const";
import { eventsEmitter } from "utils/events";
import ShopsHelper from "./ShopsHelper";

const PRODUCTS_CACHE_DURATION_IN_MS = 24 * 60 * 60 * 1000;

export const saveCategories = (list) => db.addAll(COLLECTIONS.CATEGORIES.name, list);
export const getCategoriesFromDB = () => db.getAll(COLLECTIONS.CATEGORIES.name);

export const saveItems = (list) => db.addAll(COLLECTIONS.PRODUCTS.name, list);
export const getItemsFromDB = () => db.getAll(COLLECTIONS.PRODUCTS.name);
export const updateLocalItem = async (item, shouldEmit = true) => {
  await db.update(COLLECTIONS.PRODUCTS.name, item.id, item);
  if (shouldEmit) {
    eventsEmitter.emit(EVENTS.PRODUCT_UPDATED, item);
  }
  return item;
};
export const getProductById = (id) => db.get(COLLECTIONS.PRODUCTS.name, id);

export const savePriceCategories = (list) => db.addAll(COLLECTIONS.PRICE_CATEGORIES.name, list);
export const getPriceCategoriesFromDB = () => db.getAll(COLLECTIONS.PRICE_CATEGORIES.name);

export const savePriceCategoryItems = (list) => db.addAll(COLLECTIONS.PRICE_CATEGORY_ITEMS.name, list);
export const getPriceCategoryItemsFromDB = () => db.getAll(COLLECTIONS.PRICE_CATEGORY_ITEMS.name);

export const saveAddonMappings = (list) => db.addAll(COLLECTIONS.ADDON_MAPPINGS.name, list);
export const getAddonMappingsFromDB = () => db.getAll(COLLECTIONS.ADDON_MAPPINGS.name);

export const saveChargeGroups = (list) => db.addAll(COLLECTIONS.CHARGE_GROUPS.name, list);
export const getChargeGroupsFromDB = () => db.getAll(COLLECTIONS.CHARGE_GROUPS.name);

export const saveCharges = (list) => db.addAll(COLLECTIONS.CHARGES.name, list);
export const getChargesFromDB = () => db.getAll(COLLECTIONS.CHARGES.name);

async function fetchCategoriesFromAPI() {
  return ItemService.getItemCategories();
}

async function fetchItemsFromAPI() {
  return ItemService.getItems();
}

export async function syncInitialCategoriesOnly(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const categories = await fetchCategoriesFromAPI();
      const existingDbCategories = await getCategoriesFromDB();
      const existingMap = new Map((existingDbCategories || []).map((cat) => [cat.id, cat]));

      const categoriesWithLocalConfig = (categories || []).map((cat) => {
        const existing = existingMap.get(cat.id);
        if (existing && existing.hide_from_pos !== undefined) {
          return {
            ...cat,
            hide_from_pos: existing.hide_from_pos,
          };
        }
        return cat;
      });

      const cachedItems = await getItemsFromDB();
      const enriched = enrichCategoriesWithCount(categoriesWithLocalConfig, cachedItems);
      await saveCategories(enriched);
      return enriched;
    }
    return getCategoriesFromDB();
  } catch (error) {
    console.error("Category sync failed", error);
    throw error;
  }
}

export async function syncInitialProductsOnly(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const items = await fetchItemsFromAPI();
      await saveItems(items);
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.LAST_PRODUCT_FETCH_TIME, now);
      return items;
    }
    return getItemsFromDB();
  } catch (error) {
    console.error("Product sync failed", error);
    throw error;
  }
}

export async function syncPriceCategories(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const priceCategories = await ItemService.getPriceCategories();
      await savePriceCategories(priceCategories);
      return priceCategories;
    }
    return getPriceCategoriesFromDB();
  } catch (error) {
    console.error("Price categories sync failed", error);
    throw error;
  }
}

export async function syncPriceCategoryItems(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const priceCategoryItems = await ItemService.getPriceCategoryItems();
      await savePriceCategoryItems(priceCategoryItems);
      return priceCategoryItems;
    }
    return getPriceCategoryItemsFromDB();
  } catch (error) {
    console.error("Price category items sync failed", error);
    throw error;
  }
}

export async function syncAddonMappings(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const addonMappings = await ItemService.getAddonMappings();
      await saveAddonMappings(addonMappings);
      return addonMappings;
    }
    return getAddonMappingsFromDB();
  } catch (error) {
    console.error("Addon mappings sync failed", error);
    throw error;
  }
}

export async function syncChargeGroups(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const chargeGroups = await ItemService.getChargeGroups();
      await saveChargeGroups(chargeGroups);
      return chargeGroups;
    }
    return getChargeGroupsFromDB();
  } catch (error) {
    console.error("Charge groups sync failed", error);
    throw error;
  }
}

export async function syncCharges(syncFromBE = true) {
  try {
    if (syncFromBE) {
      const charges = await ItemService.getCharges();
      await saveCharges(charges);
      return charges;
    }
    return getChargesFromDB();
  } catch (error) {
    console.error("Charges sync failed", error);
    throw error;
  }
}

async function loadProductsFromCachedDB(isPremium) {
  const cachedCategories = await getCategoriesFromDB();
  const cachedItems = await getItemsFromDB();
  const cachedPriceCategories = isPremium ? await getPriceCategoriesFromDB() : [];
  const cachedPriceCategoryItems = isPremium ? await getPriceCategoryItemsFromDB() : [];
  const cachedAddonMappings = await getAddonMappingsFromDB();
  const cachedChargeGroups = isPremium ? await getChargeGroupsFromDB() : [];
  const cachedCharges = isPremium ? await getChargesFromDB() : [];

  if (cachedCategories.length && cachedItems.length) {
    return {
      categories: enrichCategoriesWithCount(cachedCategories, cachedItems),
      items: cachedItems,
      priceCategories: cachedPriceCategories,
      priceCategoryItems: cachedPriceCategoryItems,
      addonMappings: cachedAddonMappings,
      chargeGroups: cachedChargeGroups,
      charges: cachedCharges,
      fromCache: true,
    };
  }

  return null;
}

export async function loadInitialProducts(syncFromBE = false, localRefresh = false) {
  const shopProfile = ShopsHelper.getCachedShopProfile();
  const isPremium = ShopsHelper.isPremiumShop(shopProfile);

  const now = Date.now();
  const productsLastSyncedAt = Number(localStorage.getItem(STORAGE_KEYS.LAST_PRODUCT_FETCH_TIME));
  const cacheExpired = !productsLastSyncedAt || now - productsLastSyncedAt > PRODUCTS_CACHE_DURATION_IN_MS;

  if ((!cacheExpired || localRefresh) && !syncFromBE) {
    const cachedData = await loadProductsFromCachedDB(isPremium);
    if (cachedData) return cachedData;
  }

  try {
    const [categories, items, priceCategories, priceCategoryItems, addonMappings, chargeGroups, charges] =
      await Promise.all([
        syncInitialCategoriesOnly(true),
        syncInitialProductsOnly(true),
        isPremium ? syncPriceCategories(true) : Promise.resolve([]),
        isPremium ? syncPriceCategoryItems(true) : Promise.resolve([]),
        syncAddonMappings(true),
        isPremium ? syncChargeGroups(true) : Promise.resolve([]),
        isPremium ? syncCharges(true) : Promise.resolve([]),
      ]);

    const enriched = enrichCategoriesWithCount(categories, items);
    await saveCategories(enriched);

    return {
      categories: enriched,
      items,
      priceCategories,
      priceCategoryItems,
      addonMappings,
      chargeGroups,
      charges,
      fromCache: false,
    };
  } catch (error) {
    const cachedData = await loadProductsFromCachedDB(isPremium);
    if (cachedData) return cachedData;

    throw new Error("No data available from API or IndexedDB");
  }
}

function enrichCategoriesWithCount(categories, items) {
  return categories.map((cat) => ({
    ...cat,
    count: items.filter((i) => i.category === cat.id).length,
  }));
}

export function findItemById(items, itemId) {
  return items.find((_item) => _item.id === itemId);
}

export function isAvailable(item) {
  if (!item) return false;
  if (item.is_available === false || item.hide_from_pos === true) {
    return false;
  }

  const { available_days, available_time_range } = item;

  // If both are null/empty, item is always available
  if (!available_days && !available_time_range) {
    return true;
  }

  const now = new Date();

  // Check Days
  if (available_days) {
    const currentDay = now.toLocaleDateString("en-US", { weekday: "short" });
    const daysArray = available_days.split(",").map((d) => d.trim().toLowerCase());
    if (!daysArray.includes(currentDay.toLowerCase())) {
      return false;
    }
  }

  // Check Time Range
  if (available_time_range) {
    const timeRanges = available_time_range.split(",").map((r) => r.trim());
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const isWithinAnyRange = timeRanges.some((range) => {
      const [start, end] = range.split("-").map((t) => t.trim());
      if (start && end) {
        const [startH, startM] = start.split(":").map(Number);
        const startTime = startH * 60 + startM;

        const [endH, endM] = end.split(":").map(Number);
        const endTime = endH * 60 + endM;

        return currentTime >= startTime && currentTime <= endTime;
      }
      return false;
    });

    if (!isWithinAnyRange) {
      return false;
    }
  }

  return true;
}

export async function updateLocalStockForSale(sale) {
  if (!sale || !sale.items) return;

  const updatedItems = [];

  for (const cartItem of sale.items) {
    const item = cartItem.item;
    if (!item) continue;

    const quantity = cartItem.qty || 1;
    const quantityRatio = cartItem.option?.quantityRatio || 1;

    if (item.type === PRODUCT_TYPE.INVENTORY || item.type === PRODUCT_TYPE.PRODUCTION) {
      const updated = await updateItemStock(item.id, quantity, quantityRatio);
      if (updated) updatedItems.push(updated);
    } else if (item.type === PRODUCT_TYPE.COMBO) {
      const updatedSubs = await updateSubItemStocks(
        item.details?.subItems || item.details?.sub_items,
        quantity,
        quantityRatio,
      );
      if (updatedSubs && updatedSubs.length) updatedItems.push(...updatedSubs);
    }
  }

  if (updatedItems.length > 0) {
    eventsEmitter.emit(EVENTS.PRODUCT_UPDATED, updatedItems);
  }
}

async function updateItemStock(itemId, quantity, quantityRatio) {
  const item = await getProductById(itemId);
  if (!item) return null;

  item.stock -= quantity * quantityRatio;
  await updateLocalItem(item, false);
  return item;
}

async function updateSubItemStocks(subItems, quantity, quantityRatio) {
  if (!subItems || !Array.isArray(subItems)) return [];
  const effectiveQty = quantity * quantityRatio;
  const updatedSubItems = [];

  for (const subItem of subItems) {
    const subItemForUpdate = await getProductById(subItem.itemId || subItem.item_id);
    if (subItemForUpdate) {
      subItemForUpdate.stock -= effectiveQty * (subItem.fraction || 1);
      await updateLocalItem(subItemForUpdate, false);
      updatedSubItems.push(subItemForUpdate);
    }
  }
  return updatedSubItems;
}

export async function updateLocalProductsFromPurchase(purchasedItems) {
  if (!Array.isArray(purchasedItems) || purchasedItems.length === 0) return;

  for (const purchasedItem of purchasedItems) {
    const existingProduct = await getProductById(purchasedItem?.item_id);
    const profitMargin = purchasedItem?.profit_margin;

    const existingConfig = existingProduct?.config || {};
    const config = { ...existingConfig, profit_margin: profitMargin };
    const stock = existingProduct.stock + purchasedItem.quantity;

    const updatedProduct = {
      ...existingProduct,
      cost: purchasedItem?.cost,
      price: purchasedItem?.price,
      tax: purchasedItem.tax,
      taxPercentage: purchasedItem.taxPercentage,
      stock,
      config,
    };

    await updateLocalItem(updatedProduct);
  }
}

export async function getSyncEnabledProductsFromDB() {
  const products = await getItemsFromDB();
  return (products || []).filter((product) => Boolean(product?.sync_enabled));
}

export async function filterCategoriesForCaptain() {
  const data = await getCategoriesFromDB();

  return (data || []).filter(
    (category) =>
      category?.is_available === true &&
      category?.hide_from_pos !== true &&
      category?.config?.kot_settings?.is_enable_for_captain !== false,
  );
}

export function isKotPrintEnabledForCategory(category, orderType) {
  const kotSettings = category?.config?.kot_settings;

  // Print KOT is enabled by default
  if (!kotSettings) return true;

  // Disabled KOT for the category
  if (kotSettings.is_enable_kot_print === false) return false;

  if (!orderType) return true;

  // Skip KOT by order type for the category
  const orderTypeConfig = kotSettings?.order_types?.[orderType];
  if (orderTypeConfig !== undefined) {
    return orderTypeConfig;
  }

  const cleanedOrderType = orderType.toUpperCase().replace(" ", "_");
  return kotSettings?.order_types?.[cleanedOrderType] ?? true;
}

function findCategoryById(categories, id) {
  return categories.find((category) => category?.id === id);
}

export async function getActiveProducts() {
  const categories = await getCategoriesFromDB();

  const products = await getItemsFromDB();
  const activeProducts = (products || []).filter((product) => {
    if (!product?.is_available) {
      return false;
    }

    const category = findCategoryById(categories, product?.category);
    return !(category.is_available === false || category.hide_from_pos === true);
  });
  return activeProducts;
}

export async function getActiveProductsForCaptain() {
  const categories = await getCategoriesFromDB();

  const products = await getItemsFromDB();
  const activeProducts = (products || []).filter((product) => {
    if (!product?.is_available) {
      return false;
    }

    const category = findCategoryById(categories, product?.category);
    return !(
      category?.is_available === false ||
      category?.hide_from_pos === true ||
      category?.config?.kot_settings?.is_enable_for_captain === false
    );
  });
  return activeProducts;
}
